import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import { FaTrash, FaSpinner, FaExclamationTriangle, FaUpload } from "react-icons/fa";
import Navbar from "../Navbar";
import Footer from "../Footer";
import OutletSearch from "../SelectSearch/OutletSearch";
import { CustomModal } from "../Modal/CustomModal";
import { MenuItemsGroupedTable, MenuItemsSummaryFooter } from "./ModalSubComponents";
import { useNavigate } from "react-router-dom";
import { FiUpload } from "react-icons/fi";

const baseUrl = process.env.REACT_APP_BASE_URL;

// ── Helpers ───────────────────────────────────────────────────────────────────

const formatDate = (raw) => {
    if (!raw) return "—";
    const d = new Date(raw);
    if (isNaN(d.getTime())) return raw;
    return d.toLocaleString(undefined, {
        year: "numeric", month: "short", day: "2-digit",
        hour: "2-digit", minute: "2-digit",
    });
};

// ── State badge ───────────────────────────────────────────────────────────────

const StateBadge = ({ state }) => {
    const styles = {
        saved: "bg-gray-100 text-gray-600",
        published: "bg-green-100 text-green-700",
    };
    return (
        <span className={`inline-block text-xs px-2.5 py-1 capitalize rounded-full font-medium ${styles[state] || "bg-gray-100 text-gray-600"}`}>
            {state}
        </span>
    );
};

// ── Main component ────────────────────────────────────────────────────────────

const UploadedMenuPage = () => {
    const token = localStorage.getItem("token");
    const [selectedOutlet, setSelectedOutlet] = useState(null);
    const navigate = useNavigate();

    const [menus, setMenus] = useState([]);
    const [loadingMenus, setLoadingMenus] = useState(false);
    const [menusError, setMenusError] = useState(null);
    const [rowActions, setRowActions] = useState({});

    const [selectedMenu, setSelectedMenu] = useState(null);
    const [menuItems, setMenuItems] = useState([]);
    const [loadingItems, setLoadingItems] = useState(false);
    const [itemsError, setItemsError] = useState(null);
    const [showItemsModal, setShowItemsModal] = useState(false);

    const setRowAction = (id, patch) =>
        setRowActions((prev) => ({ ...prev, [id]: { ...prev[id], ...patch } }));

    const fetchMenus = useCallback(async () => {
        if (!selectedOutlet) return;
        setLoadingMenus(true);
        setMenusError(null);
        try {
            const response = await axios.post(`${baseUrl}/menu-forversioning/get`, {
                token, outlet: selectedOutlet,
            });
            setMenus(response.data?.menus || []);
        } catch (err) {
            const message = err?.response?.data?.error || err?.response?.data?.message || "Failed to load menu versions";
            setMenusError(message);
            toast.error(message);
            setMenus([]);
        } finally {
            setLoadingMenus(false);
        }
    }, [selectedOutlet, token]);

    useEffect(() => {
        if (selectedOutlet) fetchMenus();
        else { setMenus([]); setMenusError(null); }
    }, [selectedOutlet, fetchMenus]);

    const handleRowClick = async (menu) => {
        setSelectedMenu(menu);
        setShowItemsModal(true);
        setMenuItems([]);
        setItemsError(null);
        setLoadingItems(true);
        try {
            const response = await axios.post(`${baseUrl}/menu-forversioning/get-items`, {
                menu_master_id: menu.id, token,
            });
            setMenuItems(response.data?.items || []);
        } catch (err) {
            const message = err?.response?.data?.error || err?.response?.data?.message || "Failed to load menu items";
            setItemsError(message);
            toast.error(message);
        } finally {
            setLoadingItems(false);
        }
    };

    const handleUpdateState = async (menu, e) => {
        e.stopPropagation();
        if (menu.state === "published") return;
        setRowAction(menu.id, { updating: true, error: null });
        try {
            await axios.post(`${baseUrl}/menu-forversioning/update-state`, {
                token, menu_master_id: menu.id, state: "published",
            });
            setMenus((prev) => prev.map((m) => m.id === menu.id ? { ...m, state: "published" } : m));
            fetchMenus();
            toast.success(`Version #${menu.version} published`);
        } catch (err) {
            const message = err?.response?.data?.error || err?.response?.data?.message || "Failed to update state";
            setRowAction(menu.id, { error: message });
            toast.error(message);
        } finally {
            setRowAction(menu.id, { updating: false });
        }
    };

    const handleDelete = async (menu, e) => {
        e.stopPropagation();
        setRowAction(menu.id, { deleting: true, error: null });
        try {
            await axios.post(`${baseUrl}/menu-forversioning/delete`, {
                menu_master_id: menu.id, token,
            });
            setMenus((prev) => prev.filter((m) => m.id !== menu.id));
            toast.success(`Version #${menu.version} deleted`);
            setRowActions((prev) => { const u = { ...prev }; delete u[menu.id]; return u; });
        } catch (err) {
            const message = err?.response?.data?.error || err?.response?.data?.message || "Failed to delete version";
            setRowAction(menu.id, { error: message, deleting: false });
            toast.error(message);
        }
    };

    // ── Render ────────────────────────────────────────────────────────────────
    return (
        <div className="flex flex-col min-h-screen bg-gray-50">
            <Navbar />

            <div className="flex-1 p-3 max-w-4xl mx-auto w-full">

                {/* Outlet picker */}
                <div className="flex justify-between items-center mb-2.5">
                    <div className="flex-1" />

                    <div className="flex-1 flex justify-center">
                        <OutletSearch
                            selectedOutlet={selectedOutlet}
                            setSelectedOutlet={setSelectedOutlet}
                            token={token}
                        />
                    </div>

                    <div className="flex-1 flex justify-end">
                        <button
                            className="px-3 py-2 text-sm rounded-md font-medium bg-violet-100 text-violet-800 hover:bg-violet-200 hover:shadow-md flex items-center gap-2"
                            onClick={() => navigate('/menu/upload')}
                        >
                            <FiUpload />
                            Upload
                        </button>
                    </div>
                </div>

                {/* Empty / loading / error states */}
                {!selectedOutlet && (
                    <div className="text-center text-sm text-gray-400 py-16">
                        Select an outlet to view its uploaded menu versions.
                    </div>
                )}

                {selectedOutlet && loadingMenus && (
                    <div className="flex items-center justify-center gap-2 text-sm text-gray-400 py-16">
                        <FaSpinner className="animate-spin" />
                        Loading menu versions…
                    </div>
                )}

                {selectedOutlet && !loadingMenus && menusError && (
                    <div className="flex flex-col items-center gap-2 text-sm text-red-600 py-16">
                        <FaExclamationTriangle size={18} />
                        {menusError}
                        <button
                            onClick={fetchMenus}
                            className="mt-1 px-3 py-1.5 rounded-md bg-red-100 text-red-700 hover:bg-red-200 text-sm font-medium transition"
                        >
                            Retry
                        </button>
                    </div>
                )}

                {selectedOutlet && !loadingMenus && !menusError && menus.length === 0 && (
                    <div className="text-center text-sm text-gray-400 py-16">
                        No menu versions found for this outlet.
                    </div>
                )}

                {/* Versions table */}
                {selectedOutlet && !loadingMenus && !menusError && menus.length > 0 && (
                    <div className="bg-white rounded-md shadow-md overflow-hidden">
                        <div className="px-4 py-2.5 border-b bg-gray-50 flex items-center justify-between">
                            <span className="text-sm font-semibold text-gray-700">Menu Versions</span>
                            <span className="text-xs font-medium text-gray-500 tracking-wide">{menus.length} version{menus.length !== 1 ? "s" : ""}</span>
                        </div>

                        <div className="overflow-x-auto tiny-scrollbar">
                            <table className="w-full text-sm text-center">
                                <thead className="bg-gray-100 sticky top-0 z-10">
                                    <tr>
                                        {[
                                            "Version",
                                            "Created",
                                            "Updated",
                                            "State",
                                            "Actions",
                                        ].map((header) => (
                                            <th
                                                key={header}
                                                className="px-3 py-2.5 text-xs font-semibold uppercase tracking-widest text-gray-700"
                                            >
                                                {header}
                                            </th>
                                        ))}
                                    </tr>
                                </thead>

                                <tbody className="divide-y divide-gray-100">
                                    {menus.map((menu) => {
                                        const action = rowActions[menu.id] || {};
                                        const isPublished = menu.state === "published";

                                        return (
                                            <tr
                                                key={menu.id}
                                                onClick={() => handleRowClick(menu)}
                                                className="hover:bg-gray-50 transition cursor-pointer"
                                            >
                                                <td className="px-3 py-2.5 font-medium text-gray-800">
                                                    #{menu.version}
                                                </td>
                                                <td className="px-3 py-2.5 text-gray-600">{formatDate(menu.created_at)}</td>
                                                <td className="px-3 py-2.5 text-gray-600">{formatDate(menu.updated_at)}</td>
                                                <td className="px-3 py-2.5">
                                                    <StateBadge state={menu.state} />
                                                    {action.error && (
                                                        <div className="text-xs text-red-500 mt-1">{action.error}</div>
                                                    )}
                                                </td>
                                                <td className="px-3 py-2.5">
                                                    <div className="flex items-center justify-center gap-1.5" onClick={(e) => e.stopPropagation()}>
                                                        {/* Publish button */}
                                                        <button
                                                            title={isPublished ? "Already published" : "Publish this version"}
                                                            onClick={(e) => handleUpdateState(menu, e)}
                                                            disabled={isPublished || action.updating}
                                                            className="h-7 w-7 flex items-center justify-center rounded border border-gray-300 text-blue-600 hover:bg-blue-50 disabled:opacity-30 disabled:cursor-not-allowed transition"
                                                        >
                                                            {action.updating
                                                                ? <FaSpinner size={12} className="animate-spin" />
                                                                : <FaUpload size={12} />
                                                            }
                                                        </button>

                                                        {/* Delete button */}
                                                        <button
                                                            title="Delete this version"
                                                            onClick={(e) => handleDelete(menu, e)}
                                                            disabled={action.deleting}
                                                            className="h-7 w-7 flex items-center justify-center rounded border border-gray-300 text-red-600 hover:bg-red-50 disabled:opacity-30 disabled:cursor-not-allowed transition"
                                                        >
                                                            {action.deleting
                                                                ? <FaSpinner size={12} className="animate-spin" />
                                                                : <FaTrash size={12} />
                                                            }
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    </div>
                )}
            </div>

            {/* Items modal */}
            <CustomModal
                show={showItemsModal}
                onHide={() => setShowItemsModal(false)}
                size="lg"
                centered
                scrollable
            >
                <CustomModal.Header closeButton onHide={() => setShowItemsModal(false)}>
                    <CustomModal.Title className="text-base font-semibold text-gray-800">
                        {selectedMenu ? `Version #${selectedMenu.id} — Items` : "Items"}
                    </CustomModal.Title>
                </CustomModal.Header>

                <CustomModal.Body>
                    {loadingItems && (
                        <div className="flex items-center justify-center gap-2 text-sm text-gray-400 py-12">
                            <FaSpinner className="animate-spin" />
                            Loading items…
                        </div>
                    )}

                    {!loadingItems && itemsError && (
                        <div className="flex flex-col items-center gap-2 text-sm text-red-600 py-12">
                            <FaExclamationTriangle size={18} />
                            {itemsError}
                            <button
                                onClick={() => selectedMenu && handleRowClick(selectedMenu)}
                                className="mt-1 px-3 py-1.5 rounded-md bg-red-100 text-red-700 hover:bg-red-200 text-sm font-medium transition"
                            >
                                Retry
                            </button>
                        </div>
                    )}

                    {!loadingItems && !itemsError && menuItems.length === 0 && (
                        <div className="text-center text-sm text-gray-400 py-12">
                            No items found for this version.
                        </div>
                    )}

                    {!loadingItems && !itemsError && menuItems.length > 0 && (
                        <MenuItemsGroupedTable items={menuItems} />
                    )}
                </CustomModal.Body>

                {!loadingItems && !itemsError && menuItems.length > 0 && (
                    <CustomModal.Footer>
                        <MenuItemsSummaryFooter items={menuItems} />
                    </CustomModal.Footer>
                )}
            </CustomModal>

            <Footer />
        </div>
    );
};

export default UploadedMenuPage;